/**
 * Iter-189.E Sprint 5 — Footer Control Center frontend hook.
 *
 * Mirrors `useHomepageData()` from `lib/homepageData.js`:
 *   - sessionStorage-cached (5 min TTL) so cross-page nav doesn't
 *     re-hit the API
 *   - Inflight de-dupe via a module-scoped Promise
 *   - Always returns a "safe" empty config on first paint + on API
 *     failure (lets <Footer /> render its hardcoded JSX defaults)
 */
import { useEffect, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";
const CACHE_KEY = "carsavr.footer.v1";
const CACHE_TTL_MS = 5 * 60 * 1000;

const EMPTY_CONFIG = {
  compare_options: { items: null },
  glossary_picks: { items: null },
  trust_strip: {
    badge_1_title: null,
    badge_1_copy: null,
    badge_2_title: null,
    badge_2_copy: null,
    badge_3_title: null,
    badge_3_copy: null,
  },
};

let inflight = null;

function readCache() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.ts || !parsed.data) return null;
    if (Date.now() - parsed.ts > CACHE_TTL_MS) return null;
    return parsed.data;
  } catch {
    return null;
  }
}

function writeCache(data) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data }));
  } catch {
    /* swallow */
  }
}

async function fetchOnce() {
  if (inflight) return inflight;
  inflight = axios
    .get(`${API}/footer`, { timeout: 6000 })
    .then((r) => {
      writeCache(r.data);
      return r.data;
    })
    .catch(() => EMPTY_CONFIG)
    .finally(() => {
      // Allow the next mount to re-fetch if cache expires.
      inflight = null;
    });
  return inflight;
}

export function useFooterData() {
  const [data, setData] = useState(() => readCache() || EMPTY_CONFIG);
  useEffect(() => {
    let alive = true;
    fetchOnce().then((res) => {
      if (alive) setData(res || EMPTY_CONFIG);
    });
    return () => {
      alive = false;
    };
  }, []);
  return data;
}

/** Returns the curated `compare_options.items` list, or `null` if no
 *  admin override exists (caller falls back to hardcoded COMPARISONS). */
export function useCompareOverrides() {
  const data = useFooterData();
  return data?.compare_options?.items || null;
}

/** Returns the curated `glossary_picks.items` list, or `null`. */
export function useGlossaryOverrides() {
  const data = useFooterData();
  return data?.glossary_picks?.items || null;
}

/** Returns the trust-strip override fields (any of which may be null,
 *  meaning "fall back to the JSX literal for that slot"). */
export function useTrustStripOverrides() {
  const data = useFooterData();
  return data?.trust_strip || EMPTY_CONFIG.trust_strip;
}
